import React from 'react';
import { Card, CardSkeleton } from '@/components/ui';
import type { UsageMetrics } from '@/services/admin/AdminService';

interface UsageMetricsCardProps {
  metrics: UsageMetrics | null;
  loading: boolean;
}

interface MetricItemProps {
  label: string;
  value: number | string;
  subLabel?: string;
}

const MetricItem: React.FC<MetricItemProps> = ({ label, value, subLabel }) => (
  <div className="text-center p-4">
    <p className="text-3xl font-bold text-primary-600">{value}</p>
    <p className="text-sm font-medium text-secondary-700">{label}</p>
    {subLabel && <p className="text-xs text-secondary-500">{subLabel}</p>}
  </div>
);

export const UsageMetricsCard: React.FC<UsageMetricsCardProps> = ({
  metrics,
  loading,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (!metrics) {
    return (
      <Card padding="lg">
        <p className="text-secondary-500">No usage metrics available</p>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        Usage Overview
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricItem label="Total Projects" value={metrics.total_projects} />
        <MetricItem label="Total Users" value={metrics.total_users} />
        <MetricItem label="Total Jobs" value={metrics.total_jobs} />
        <MetricItem
          label="Active Today"
          value={metrics.active_users_today}
          subLabel="users"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Project Stats */}
        <div className="border border-secondary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 mb-3">
            Projects
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-secondary-600">Created today</span>
              <span className="font-medium">
                {metrics.projects_created_today}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Created this week</span>
              <span className="font-medium">
                {metrics.projects_created_this_week}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Created this month</span>
              <span className="font-medium">
                {metrics.projects_created_this_month}
              </span>
            </div>
          </div>
          {metrics.projects_by_status &&
            Object.keys(metrics.projects_by_status).length > 0 && (
              <div className="mt-4 pt-3 border-t border-secondary-100">
                <h5 className="text-xs font-medium text-secondary-500 mb-2">
                  By Status
                </h5>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(metrics.projects_by_status).map(
                    ([status, count]) => (
                      <span
                        key={status}
                        className="px-2 py-1 text-xs bg-secondary-100 text-secondary-700 rounded"
                      >
                        {status}: {count}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
        </div>

        {/* User Stats */}
        <div className="border border-secondary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 mb-3">Users</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-secondary-600">Active this week</span>
              <span className="font-medium">
                {metrics.active_users_this_week}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Jobs created today</span>
              <span className="font-medium">{metrics.jobs_created_today}</span>
            </div>
          </div>
          {metrics.users_by_role &&
            Object.keys(metrics.users_by_role).length > 0 && (
              <div className="mt-4 pt-3 border-t border-secondary-100">
                <h5 className="text-xs font-medium text-secondary-500 mb-2">
                  By Role
                </h5>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(metrics.users_by_role).map(
                    ([role, count]) => (
                      <span
                        key={role}
                        className="px-2 py-1 text-xs bg-secondary-100 text-secondary-700 rounded"
                      >
                        {role}: {count}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </Card>
  );
};

export default UsageMetricsCard;
